import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CreateIcon from "@material-ui/icons/Create";
import PhoneIcon from "@material-ui/icons/Phone";
import EmailIcon from "@material-ui/icons/Email";
import BlockIcon from "@material-ui/icons/Block";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import EditIcon from "@material-ui/icons/Edit";
import MicOffIcon from "@material-ui/icons/MicOff";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import MicIcon from "@material-ui/icons/Mic";
import formatSerializedId from "../../utils/formatSerializedId";
import { i18n } from "../../translate/i18n";
import ModalImageCors from "../ModalImageCors";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { Switch } from "@material-ui/core";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { TagsKanbanContainer } from "../TagsKanbanContainer";

const drawerWidth = 340;

const useStyles = makeStyles((theme) => ({
  drawer: { width: drawerWidth, flexShrink: 0 },
  drawerPaper: {
    zIndex: 1800,
    width: drawerWidth,
    display: "flex",
    flexDirection: "column",
    border: "none",
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "-4px 0 32px rgba(0,0,0,0.5)"
      : "-4px 0 32px rgba(0,102,255,0.10)",
  },

  // ── Header ─────────────────────────────────────────────────────────────────
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.2, 1.5),
    background: theme.palette.type === "dark" ? "#0A1628" : "#ffffff",
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.12)"
      : "1px solid rgba(0,102,255,0.08)",
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 10,
    background: "rgba(0,102,255,0.07)",
    color: "#0066FF",
    "&:hover": { background: "rgba(0,102,255,0.14)" },
  },

  // ── Scroll content ─────────────────────────────────────────────────────────
  content: {
    flex: 1,
    overflowY: "auto",
    background: theme.palette.type === "dark"
      ? "linear-gradient(180deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(180deg, #F0F6FF 0%, #EBF3FF 100%)",
    ...theme.scrollbarStyles,
  },

  // ── Hero ───────────────────────────────────────────────────────────────────
  hero: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)"
      : "linear-gradient(135deg, #EBF3FF 0%, #C2DCFF 100%)",
    padding: theme.spacing(2.5, 2, 2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.12)"
      : "1px solid rgba(0,102,255,0.10)",
    position: "relative",
  },
  heroBgCircle: {
    position: "absolute", right: -20, top: -20,
    width: 140, height: 140, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(0,102,255,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  avatarWrap: {
    position: "relative",
    width: 88, height: 88,
    marginBottom: theme.spacing(1.5),
  },
  avatarImg: {
    width: 88, height: 88,
    borderRadius: 22,
    border: theme.palette.type === "dark"
      ? "3px solid rgba(0,140,255,0.35)"
      : "3px solid rgba(0,102,255,0.20)",
    boxShadow: "0 8px 24px rgba(0,102,255,0.18)",
    objectFit: "cover",
    display: "block",
  },
  onlineDot: {
    position: "absolute", bottom: 4, right: 4,
    width: 14, height: 14, borderRadius: "50%",
    background: "#00D084",
    border: "2px solid white",
    boxShadow: "0 0 6px rgba(0,208,132,0.6)",
  },
  contactName: {
    fontWeight: 800, fontSize: "1.1rem",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
    display: "flex", alignItems: "center", gap: 6,
    cursor: "pointer",
    "&:hover": { color: "#0066FF" },
    transition: "color 0.15s",
  },
  contactBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "rgba(0,208,132,0.12)", color: "#00A86B",
    border: "1px solid rgba(0,208,132,0.3)",
    borderRadius: 20, padding: "2px 10px",
    fontSize: "0.65rem", fontWeight: 700, marginTop: 4,
  },
  contactBadgeDot: {
    width: 5, height: 5, borderRadius: "50%",
    background: "#00D084", boxShadow: "0 0 4px rgba(0,208,132,0.8)",
  },

  // ── Info row ───────────────────────────────────────────────────────────────
  infoRow: {
    display: "flex", alignItems: "center", gap: 8,
    padding: theme.spacing(0.6, 0),
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "rgba(10,22,40,0.65)",
  },
  infoIcon: { fontSize: 14, color: "#0066FF", flexShrink: 0 },

  // ── Action buttons ─────────────────────────────────────────────────────────
  actionRow: {
    display: "flex", gap: 8, marginTop: theme.spacing(1.5), width: "100%",
  },
  btnEdit: {
    flex: 1, borderRadius: 10, fontWeight: 700, fontSize: "0.75rem",
    textTransform: "none", border: "1px solid rgba(0,102,255,0.3)",
    color: "#0066FF", padding: theme.spacing(0.7, 1),
    "&:hover": { background: "rgba(0,102,255,0.07)" },
  },
  btnBlock: {
    flex: 1, borderRadius: 10, fontWeight: 700, fontSize: "0.75rem",
    textTransform: "none", border: "1px solid rgba(239,68,68,0.3)",
    color: "#EF4444", padding: theme.spacing(0.7, 1),
    "&:hover": { background: "rgba(239,68,68,0.06)" },
  },
  btnUnblock: {
    flex: 1, borderRadius: 10, fontWeight: 700, fontSize: "0.75rem",
    textTransform: "none", border: "1px solid rgba(0,208,132,0.3)",
    color: "#00A86B", padding: theme.spacing(0.7, 1),
    "&:hover": { background: "rgba(0,208,132,0.06)" },
  },

  // ── Audio toggle ───────────────────────────────────────────────────────────
  audioRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)"
      : "1px solid rgba(0,102,255,0.08)",
    borderRadius: 12, padding: theme.spacing(1, 1.5),
    margin: theme.spacing(1.5, 1.5, 0),
  },
  audioLabel: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: "0.8rem", fontWeight: 600,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#334155",
  },
  audioIcon: { fontSize: 16, color: "#0066FF" },

  // ── Section ────────────────────────────────────────────────────────────────
  section: {
    margin: theme.spacing(1.5, 1.5, 0),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)"
      : "1px solid rgba(0,102,255,0.08)",
    borderRadius: 14,
    overflow: "hidden",
  },
  sectionHeader: {
    display: "flex", alignItems: "center", gap: 8,
    padding: theme.spacing(1.2, 1.5),
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(0,102,255,0.07)",
  },
  sectionDot: {
    width: 7, height: 7, borderRadius: "50%", background: "#0066FF", flexShrink: 0,
  },
  sectionTitle: {
    fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.45)",
  },
  sectionBody: { padding: theme.spacing(1.2, 1.5) },

  extraInfoItem: {
    borderRadius: 10,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#F8FAFF",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.05)"
      : "1px solid rgba(0,102,255,0.07)",
    padding: theme.spacing(0.8, 1.2),
    marginBottom: theme.spacing(0.8),
  },
  extraInfoLabel: {
    fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.4px", color: "#0066FF", marginBottom: 2,
  },
  extraInfoValue: { fontSize: "0.8rem" },

  spacer: { height: theme.spacing(2) },
  btnResumoIA: {
    width: "100%", borderRadius: 10, fontWeight: 700, fontSize: "0.78rem",
    background: "linear-gradient(135deg, #0066FF, #00A3FF)",
    color: "#fff", border: "none", padding: "9px 14px",
    cursor: "pointer", marginTop: 8, display: "flex",
    alignItems: "center", justifyContent: "center", gap: 6,
    boxShadow: "0 4px 14px rgba(0,102,255,0.2)",
  },
  resumoBox: {
    marginTop: 10, padding: "14px", borderRadius: 12,
    background: theme.palette.type === "dark" ? "rgba(0,102,255,0.08)" : "#F0F7FF",
    border: "1px solid rgba(0,102,255,0.2)",
  },
  resumoTitle: {
    fontWeight: 700, fontSize: "0.76rem", color: "#0066FF",
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10,
  },
  resumoItem: {
    display: "flex", alignItems: "flex-start", gap: 8,
    padding: "6px 0",
    borderBottom: "1px solid rgba(0,102,255,0.08)",
    fontSize: "0.78rem",
  },
  resumoLabel: {
    fontWeight: 700, color: "#475569", minWidth: 90, flexShrink: 0,
    fontSize: "0.74rem",
  },
  resumoValue: {
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#0A1628",
    lineHeight: 1.5,
  },
  btnOpportunities: {
    width: "100%", borderRadius: 12, fontWeight: 700, fontSize: "0.8rem",
    textTransform: "none", marginTop: theme.spacing(1.5),
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff", padding: theme.spacing(1, 2),
    boxShadow: "0 4px 14px rgba(0,102,255,0.28)",
    "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #007ACC 100%)" },
  },
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
  const classes = useStyles();
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const { get } = useCompanySettings();
  const [hideNum, setHideNum] = useState(false);
  const { user } = useContext(AuthContext);
  const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);
  const [showResumo, setShowResumo] = useState(false);
  const [resumoIA, setResumoIA] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const lgpdHideNumber = await get({ column: "lgpdHideNumber" });
      if (lgpdHideNumber === "enabled") setHideNum(true);
    }
    fetchData();
  }, []);

  useEffect(() => { setOpenForm(false); }, [open, contact]);

  const handleContactToggleAcceptAudio = async () => {
    try {
      const res = await api.put("/contacts/toggleAcceptAudio/" + ticket.contact.id);
      setAcceptAudio(res.data.acceptAudioMessage);
    } catch (err) { toastError(err); }
  };

  const handleBlockContact = async (contactId) => {
    try {
      await api.put("/contacts/block/" + contactId, { active: false });
      toast.success("Contato bloqueado");
    } catch (err) { toastError(err); }
  };

  const handleUnBlockContact = async (contactId) => {
    try {
      await api.put("/contacts/block/" + contactId, { active: true });
      toast.success("Contato desbloqueado");
    } catch (err) { toastError(err); }
  };

  if (loading) return null;

  const phone = hideNum && user.profile === "user"
    ? formatSerializedId(contact.number).slice(0, -6) + "**-**" + contact.number.slice(-2)
    : formatSerializedId(contact.number);

  const handleGerarResumo = async () => {
    if (!ticket?.id) return;
    setLoadingResumo(true);
    setShowResumo(true);
    try {
      const { data } = await api.get(`/prompt/resumo/${ticket.id}`);
      if (data.erro) { setResumoIA({ erro: data.erro }); setLoadingResumo(false); return; }
      setResumoIA(data.resumo);
    } catch (e) {
      setResumoIA({ erro: "Erro ao gerar resumo com IA." });
    }
    setLoadingResumo(false);
  };
return (
    <>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        PaperProps={{ style: { position: "absolute" } }}
        BackdropProps={{ style: { position: "absolute" } }}
        ModalProps={{
          container: document.getElementById("drawer-container"),
          style: { position: "absolute" },
        }}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* HEADER */}
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {i18n.t("contactDrawer.header")}
          </Typography>
          <IconButton size="small" className={classes.closeBtn} onClick={handleDrawerClose}>
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>

        {loading ? (
          <ContactDrawerSkeleton classes={classes} />
        ) : (
          <div className={classes.content}>

            {/* HERO */}
            <div className={classes.hero}>
              <div className={classes.heroBgCircle} />
              <div className={classes.avatarWrap}>
                <ModalImageCors imageUrl={contact?.urlPicture} />
                <div className={classes.onlineDot} />
              </div>
              <Typography className={classes.contactName} onClick={() => setOpenForm(true)}>
                {contact.name}
                <EditIcon style={{ fontSize: 14, opacity: 0.6 }} />
              </Typography>
              <div className={classes.contactBadge}>
                <div className={classes.contactBadgeDot} />
                CONTATO ATIVO
              </div>
              <div style={{ marginTop: 12, width: "100%" }}>
                {phone && (
                  <div className={classes.infoRow}>
                    <PhoneIcon className={classes.infoIcon} />
                    <span>{phone}</span>
                  </div>
                )}
                {contact.email && (
                  <div className={classes.infoRow}>
                    <EmailIcon className={classes.infoIcon} />
                    <Link href={"mailto:" + contact.email} style={{ fontSize: "0.8rem", color: "#0066FF" }}>
                      {contact.email}
                    </Link>
                  </div>
                )}
              </div>
              <div className={classes.actionRow}>
                <Button className={classes.btnEdit} startIcon={<EditIcon style={{ fontSize: 14 }} />}
                  onClick={() => setModalOpen(!openForm)}>
                  {i18n.t("contactDrawer.buttons.edit")}
                </Button>
                <Button
                  className={contact.active ? classes.btnBlock : classes.btnUnblock}
                  startIcon={contact.active
                    ? <BlockIcon style={{ fontSize: 14 }} />
                    : <LockOpenIcon style={{ fontSize: 14 }} />}
                  onClick={() => contact.active
                    ? handleBlockContact(contact.id)
                    : handleUnBlockContact(contact.id)}
                >
                  {contact.active ? "Bloquear" : "Desbloquear"}
                </Button>
              </div>
              <Button
                className={classes.btnOpportunities}
                startIcon={<TrendingUpIcon style={{ fontSize: 16 }} />}
                onClick={() => history.push("/opportunities?contactId=" + contact.id)}
              >
                Ver Oportunidades de Venda
              </Button>
              {/* RESUMO IA */}
              <button className={classes.btnResumoIA} onClick={handleGerarResumo}>
                🧠 {loadingResumo ? "Analisando conversa..." : "Resumo da IA"}
              </button>
              {showResumo && resumoIA && (
                <div className={classes.resumoBox}>
                  <div className={classes.resumoTitle}>📋 Resumo do Lead</div>
                  {resumoIA.erro ? (
                    <div style={{ fontSize: "0.78rem", color: "#EF4444" }}>{resumoIA.erro}</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        { icon: "🎯", label: "Interesse", value: resumoIA.interesse },
                        { icon: "💰", label: "Orçamento", value: resumoIA.orcamento },
                        { icon: "⏰", label: "Urgência", value: resumoIA.urgencia },
                        { icon: "🚫", label: "Objeções", value: resumoIA.objecoes },
                        { icon: "📝", label: "Resumo", value: resumoIA.resumo },
                        { icon: "➡️", label: "Próx. Passo", value: resumoIA.proximoPasso },
                      ].filter(i => i.value && i.value !== "null").map((item, idx) => (
                        <div key={idx} className={classes.resumoItem}>
                          <span className={classes.resumoLabel}>{item.icon} {item.label}</span>
                          <span className={classes.resumoValue}>{item.value}</span>
                        </div>
                      ))}
                      {resumoIA.qualificacao && (
                        <div style={{ marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: resumoIA.qualificacao === "quente" ? "rgba(239,68,68,0.08)" : resumoIA.qualificacao === "morno" ? "rgba(245,158,11,0.08)" : "rgba(100,116,139,0.08)", border: `1px solid ${resumoIA.qualificacao === "quente" ? "rgba(239,68,68,0.2)" : resumoIA.qualificacao === "morno" ? "rgba(245,158,11,0.2)" : "rgba(100,116,139,0.2)"}` }}>
                          <span style={{ fontWeight: 700, fontSize: "0.74rem", color: "#475569" }}>📊 Qualificação</span>
                          <span style={{ fontWeight: 800, fontSize: "0.78rem", color: resumoIA.qualificacao === "quente" ? "#EF4444" : resumoIA.qualificacao === "morno" ? "#F59E0B" : "#64748B", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {resumoIA.qualificacao === "quente" ? "🔥 Lead Quente" : resumoIA.qualificacao === "morno" ? "🌡️ Lead Morno" : "❄️ Lead Frio"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <button onClick={() => setShowResumo(false)} style={{ marginTop: 12, width: "100%", background: "none", border: "1px solid rgba(0,102,255,0.2)", borderRadius: 8, padding: "7px", fontSize: "0.74rem", color: "#0066FF", cursor: "pointer", fontWeight: 700 }}>Fechar resumo ×</button>
                </div>
              )}
              {contact.id && openForm && (
                <div style={{ marginTop: 12, width: "100%" }}>
                  <ContactForm initialContact={contact} onCancel={() => setOpenForm(false)} />
                </div>
              )}
            </div>

            {/* AUDIO TOGGLE */}
            <div className={classes.audioRow}>
              <span className={classes.audioLabel}>
                {acceptAudioMessage
                  ? <MicIcon className={classes.audioIcon} />
                  : <MicOffIcon className={classes.audioIcon} />}
                {i18n.t("ticketOptionsMenu.acceptAudioMessage")}
              </span>
              <Switch
                size="small"
                checked={acceptAudioMessage}
                onChange={handleContactToggleAcceptAudio}
                color="primary"
              />
            </div>

            {/* ETAPA KANBAN */}
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionDot} />
                <Typography className={classes.sectionTitle}>Etapa Kanban</Typography>
              </div>
              <div className={classes.sectionBody}>
                <TagsKanbanContainer ticket={ticket} />
              </div>
            </div>

            {/* OBSERVACOES */}
            <div className={classes.section}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionDot} />
                <Typography className={classes.sectionTitle}>
                  {i18n.t("ticketOptionsMenu.appointmentsModal.title")}
                </Typography>
              </div>
              <div className={classes.sectionBody}>
                <ContactNotes ticket={ticket} />
              </div>
            </div>

            {/* INFORMACOES EXTRAS */}
            {contact?.extraInfo?.length > 0 && (
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <div className={classes.sectionDot} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("contactDrawer.extraInfo")}
                  </Typography>
                </div>
                <div className={classes.sectionBody}>
                  {contact.extraInfo.map((info) => (
                    <div key={info.id} className={classes.extraInfoItem}>
                      <div className={classes.extraInfoLabel}>{info.name}</div>
                      <Typography component="div" className={classes.extraInfoValue} noWrap style={{ paddingTop: 2 }}>
                        <MarkdownWrapper>{info.value}</MarkdownWrapper>
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ContactModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              contactId={contact.id}
            />

            <div className={classes.spacer} />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default ContactDrawer;
